'use client';

import { Badge, Box, Flex, Icon, Text } from '@chakra-ui/react';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { memo, useCallback, useMemo, useState } from 'react';

import { ACTION_CATALOG } from '@features/workflows/constants';
import { type ActionNodeData, PickerMode } from '@features/workflows/types';
import { useWorkflowCallbacks } from '@features/workflows/WorkflowContext';

import NodeHoverActions from './NodeHoverActions';

interface ActionNodeComponentProps extends NodeProps {
  data: ActionNodeData;
}

const ActionNode = ({ id, data, dragging }: ActionNodeComponentProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { onOpenPicker, onOpenConfig, onDeleteNode } = useWorkflowCallbacks();
  const { label, description, actionType } = data;

  const NodeIcon = useMemo(
    () => ACTION_CATALOG.find((c) => c.actionType === actionType)?.icon,
    [actionType],
  );

  const handleAdd = useCallback(
    (nodeId: string) => onOpenPicker(nodeId, PickerMode.Add),
    [onOpenPicker],
  );

  const handleReplace = useCallback(
    (nodeId: string) => onOpenPicker(nodeId, PickerMode.Replace),
    [onOpenPicker],
  );

  const handleClick = useCallback(() => {
    onOpenConfig(id);
  }, [onOpenConfig, id]);

  return (
    <Box
      position="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <NodeHoverActions
        nodeId={id}
        isHovered={isHovered && dragging !== true}
        onAdd={handleAdd}
        onReplace={handleReplace}
        onDelete={onDeleteNode}
      />
      <Flex
        direction="column"
        bg="surface.background"
        borderWidth="1px"
        borderColor="border.light"
        borderRadius="xl"
        w="280px"
        overflow="hidden"
        cursor="pointer"
        boxShadow="xs"
        _hover={{ boxShadow: 'sm' }}
        onClick={handleClick}
      >
        <Flex px={3} pt={2} pb={1} alignItems="center" gap={2}>
          <Badge
            size="xs"
            px={2}
            borderRadius="full"
            bg="yellow.100"
            color="yellow.700"
            fontSize="2xs"
            fontWeight="semibold"
          >
            Action
          </Badge>
        </Flex>
        <Flex px={3} pb={3} gap={2} alignItems="flex-start">
          {NodeIcon != null && (
            <Icon mt="2px" color="text.secondary" flexShrink={0}>
              <NodeIcon size={16} />
            </Icon>
          )}
          <Flex direction="column" gap={0.5} minW={0}>
            <Text fontSize="sm" fontWeight="medium" truncate>
              {label}
            </Text>
            <Text fontSize="xs" color="text.secondary" truncate>
              {description}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: 8,
          height: 8,
          background: 'var(--chakra-colors-gray-400)',
          border: '2px solid var(--chakra-colors-gray-200)',
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{
          width: 8,
          height: 8,
          background: 'var(--chakra-colors-gray-400)',
          border: '2px solid var(--chakra-colors-gray-200)',
        }}
      />
    </Box>
  );
};

export default memo(ActionNode);
